import React, { useEffect, useState } from "react";
import { apiService, handleApiError } from "../../services/axiosService";
import Loader from "../../Layouts/Loader/Loader";
import BuilderTable from "../../Layouts/BuilderTable";
import Pagination from "../ApplicationUsers/Pagination";
import Input from "../../components/FormComponents/Input";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";

const DEFAULT_LIMIT = 10;
const USER_ACTIVITY_ACTION_OPTIONS = [
  { id: "", name: "All Actions" },
  { id: "create", name: "Create" },
  { id: "update", name: "Update" },
  { id: "delete", name: "Delete" },
  { id: "reactivate", name: "Reactivate" }
];

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const getActionPillStyle = (action) => {
  const normalizedAction = String(action || "").toLowerCase();

  const actionStyleMap = {
    create: { background: "#dcfce7", color: "#166534", border: "1px solid #86efac" },
    update: { background: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" },
    delete: { background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" },
    reactivate: { background: "#ede9fe", color: "#5b21b6", border: "1px solid #c4b5fd" }
  };

  return actionStyleMap[normalizedAction] || { background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" };
};

const getStatusPillStyle = (statusCode) => {
  const code = Number(statusCode);

  if (!Number.isNaN(code)) {
    if (code >= 200 && code < 300) {
      return { background: "#dcfce7", color: "#166534", border: "1px solid #86efac" };
    }

    if (code >= 400) {
      return { background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" };
    }
  }

  return { background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" };
};

const toTitleCase = (value) => String(value || "-").replace(/_/g, " ");

const UserActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_LIMIT
  });
  const [filters, setFilters] = useState({
    search: "",
    action: ""
  });
  const [searchInput, setSearchInput] = useState("");
  const [pageTrack, setPageTrack] = useState({
    currPage: 1,
    totalPages: 1
  });

  const fetchUserActivityLogs = async (page = pagination.page, limit = pagination.limit, nextFilters = filters) => {
    try {
      setIsLoading(true);

      const params = {
        page,
        limit,
        search: nextFilters.search || undefined,
        action: nextFilters.action || undefined
      };

      const response = await apiService.get("/user-services/users/audit-logs", {
        params
      });

      const data = Array.isArray(response?.data) ? response.data : [];
      const meta = response?.meta || {};

      setLogs(
        data.map((log) => ({
          id: log.id,
          action: (
            <span className="status_pill" style={getActionPillStyle(log.action)}>
              {toTitleCase(log.action)}
            </span>
          ),
          targetUserEmail: log.target_user_email || "-",
          performedByName: log.performed_by_name || "-",
          performedByEmail: log.performed_by_email || "-",
          responseStatus: (
            <span className="status_pill" style={getStatusPillStyle(log.response_status)}>
              {log.response_status ?? "-"}
            </span>
          ),
          createdAt: formatDateTime(log.created_at)
        }))
      );

      setPageTrack({
        currPage: meta.page || page,
        totalPages: meta.total_pages || 1
      });
    } catch (error) {
      handleApiError(error);
      setLogs([]);
      setPageTrack({
        currPage: 1,
        totalPages: 1
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserActivityLogs(pagination.page, pagination.limit, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const normalizedSearch = searchInput.trim();
      setFilters((prev) => {
        if (prev.search === normalizedSearch) {
          return prev;
        }

        return { ...prev, search: normalizedSearch };
      });
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const onPageChangeAPI = (pageNumber) => {
    setPagination((prev) => ({ ...prev, page: pageNumber }));
  };

  const onLimitChangeAPI = (newLimit) => {
    setPagination({
      page: 1,
      limit: newLimit
    });
  };

  const onActionFilterChange = (selectedOption) => {
    const selectedAction = selectedOption?.id || "";
    setFilters((prev) => ({ ...prev, action: selectedAction }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const columns = [
    { key: "performedByName", header: "Performed By" },
    { key: "performedByEmail", header: "Performed By Email" },
    { key: "action", header: "Action" },
    { key: "targetUserEmail", header: "Target User Email" },
    { key: "responseStatus", header: "Status" },
    { key: "createdAt", header: "Created At" }
  ];

  return (
    <div className="h_100 w_100 p_2">
      <h2 className="m_4">User Activity Logs</h2>
      <div className="d_flex justify_between gap_3 px_5 pb_3">
        <div className="position_relative" style={{ minWidth: "280px" }}>
          <Input
            placeholder="Search User Activity"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            containerStyle={{ position: "relative", width: "100%" }}
            inputBoxStyle={{ paddingLeft: "28px", height: "36px" }}
          />
          <MaterialIcon
            name="search"
            className="search position_absolute font_size_large"
            style={{ top: "8px", left: "8px" }}
          />
        </div>

        <div style={{ minWidth: "220px" }}>
          <SingleSelectBox
            options={USER_ACTIVITY_ACTION_OPTIONS}
            valueKey="id"
            labelKey="name"
            selectedData={filters.action}
            onChange={onActionFilterChange}
            placeholder="Select Action"
          />
        </div>
      </div>

      {isLoading ? (
        <Loader width={70} />
      ) : (
        <div className="org-dashboard-page-body" style={{ height: "calc(100% - 160px)" }}>
          <BuilderTable
            columns={columns}
            data={logs}
            noDataMessage="No User Activity Logs Found"
            style={{ tableLayout: "auto", minWidth: "1100px" }}
          />
          <div className="p_3 bottom_border">
            <Pagination
              pageTrack={pageTrack}
              pagination={pagination}
              onPageChange={onPageChangeAPI}
              onLimitChange={onLimitChangeAPI}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserActivityLogs;
